import groupEndpoints from '@/lib/api/endpoints/groupEndpoints';
import type { Group } from '@/lib/models/group';
import { GroupSchema } from '@/lib/models/group';
import type { ODataResponse } from '@/lib/models/common';
import { zodValidator } from '@/lib/utils/zodValidator';

/**
 * Interfaccia per il service dei Groups
 * Mantiene esattamente gli stessi nomi dei metodi del JavaScript originale
 * Wrapper che estrae automaticamente i dati dalle response Fetch
 */
export interface GroupsService {
	/**
	 * Recupera la lista dei gruppi con query OData
	 * @param query - Query string OData
	 * @returns Promise con response OData contenente array di Group
	 */
	getGroups(query: string): Promise<ODataResponse<Group>>;

	/**
	 * Recupera un singolo gruppo per ID
	 * @param id - ID del gruppo
	 * @returns Promise con singolo Group
	 */
	getGroup(id: number): Promise<Group>;

	/**
	 * Aggiorna un gruppo esistente
	 * @param id - ID del gruppo da aggiornare
	 * @param payload - Dati da aggiornare
	 * @returns Promise con Group aggiornato
	 */
	updateGroup(id: number, payload: Group): Promise<Group>;

	/**
	 * Crea un nuovo gruppo
	 * @param payload - Dati del nuovo gruppo
	 * @returns Promise con Group creato
	 */
	postGroup(payload: Group): Promise<Group>;

	/**
	 * Elimina un gruppo
	 * @param id - ID del gruppo da eliminare
	 * @returns Promise void
	 */
	deleteGroup(id: number): Promise<void>;

	/**
	 * Recupera i lavoratori di un gruppo
	 * @param groupId - ID del gruppo
	 * @returns Promise con array di GroupWorkers (con expand)
	 */
	getWorkers(groupId: number): Promise<any[]>;
}

/**
 * Implementazione del service per i Groups
 * Mantiene esattamente gli stessi nomi dei metodi del JavaScript originale
 * Estrae automaticamente la proprietà .data dalle response Fetch
 */
const groupsService: GroupsService = {
	async getGroups(query: string): Promise<ODataResponse<Group>> {
		const response = (await groupEndpoints.getGroups(query)).data;
		// Validazione runtime centralizzata (attiva solo in development)
		zodValidator.validateODataResponse(response, GroupSchema, 'Group.getAll');
		return response;
	},

	async getGroup(id: number): Promise<Group> {
		const rawData = (await groupEndpoints.getGroup(id)).data;
		// Validazione runtime centralizzata (attiva solo in development)
		zodValidator.validateSingle(rawData, GroupSchema, `Group.getById(${id})`);
		return rawData;
	},

	async updateGroup(id: number, payload: Group): Promise<Group> {
		const rawData = (await groupEndpoints.updateGroup(id, payload)).data;
		// Validazione runtime centralizzata (attiva solo in development)
		zodValidator.validateSingle(rawData, GroupSchema, `Group.updateById(${id})`);
		return rawData;
	},

	async postGroup(payload: Group): Promise<Group> {
		const rawData = (await groupEndpoints.postGroup(payload)).data;
		// Validazione runtime centralizzata (attiva solo in development)
		zodValidator.validateSingle(rawData, GroupSchema, 'Group.create');
		return rawData;
	},

	async deleteGroup(id: number): Promise<void> {
		return (await groupEndpoints.deleteGroup(id)).data;
	},

	async getWorkers(groupId: number): Promise<any[]> {
		return (await groupEndpoints.getWorkers(groupId)).data.value;
	},
};

export default groupsService;
